import React, { useState, useMemo, useEffect, useContext } from 'react';
import { 
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  FormControl, InputLabel, MenuItem, Typography, 
  OutlinedInput, Chip, Checkbox, ListItemText, Button, IconButton, Tooltip,
  ToggleButton, ToggleButtonGroup, CircularProgress, Divider,
  Card, CardContent, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import ManagedModal from './ManagedModal';
import Grid from './ContainerGrid';
import SaveIcon from '@mui/icons-material/Save';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TableViewIcon from '@mui/icons-material/TableView';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GridViewIcon from '@mui/icons-material/GridView';
import CodeIcon from '@mui/icons-material/Code'; 

import { Chart } from "react-google-charts";
import AuthContext from './auth/Context';
import { useQuickAction } from './DataQuickActionContext'; 
import DynamicCustomPivot from './DynamicCustomPivot';
import CodeEditor from './CodeEditor'; 
import TextField from "./OptimizedTextField"
import Select from "./ManagedDropdown";

const IOS_BLUE = '#007aff';

const macScrollbarSx = {
  '&::-webkit-scrollbar': { width: '5px', height: '5px' },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: '10px', transition: 'background-color 0.3s' },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.3)' },
  scrollbarWidth: 'thin', 
  scrollbarColor: 'rgba(0,0,0,0.15) transparent' 
};

const HELP_TEXT_CONTENT = `Tài liệu hướng dẫn Tùy chỉnh Giao diện (Custom View)

Trình soạn thảo này cho phép bạn sử dụng **React**, **Material-UI (MUI)** và **Google Charts** để thiết kế lại hoàn toàn cách hiển thị dữ liệu báo cáo. Mã của bạn sẽ hoạt động như phần thân (body) của một React Functional Component. 

Bạn **bắt buộc phải sử dụng từ khóa \`return\`** để trả về giao diện (JSX).

#### 🛠 Các biến (Variables) có sẵn để sử dụng:

**1. \`pivotData\` (Object):** Dữ liệu ma trận đã được hệ thống tính toán và gom nhóm sẵn. Đây là biến quan trọng nhất.
* \`pivotData.rowKeys\`: Mảng các nhóm Dòng. Mỗi phần tử có dạng \`{ str: 'Chuỗi JSON', arr: ['Giá trị 1', 'Giá trị 2'] }\`.
* \`pivotData.colKeys\`: Mảng tên các Cột chia (VD: \`['2023', '2024']\` hoặc \`['Tổng']\` nếu không chia cột).
* \`pivotData.grandTotal\`: Tổng cộng toàn bộ. Cách lấy dữ liệu: \`pivotData.grandTotal['Mã_Cột_Giá_Trị']\`.
* \`pivotData.matrix\`: Ma trận dữ liệu chi tiết. Cấu trúc truy xuất: \`matrix[row.str][colName]['Mã_Cột_Giá_Trị']\`.
* \`pivotData.calculateAgg(stats)\`: **Hàm cực kỳ quan trọng.** Các giá trị trong ma trận được lưu dưới dạng \`{ sum: 100, count: 5 }\`. Bạn phải bọc nó qua hàm này để hệ thống tự trả về con số cuối cùng dựa trên phép tính đang chọn (Sum/Count/Avg). VD: \`pivotData.calculateAgg(pivotData.grandTotal['doanh_thu'])\`.

**2. \`configMeta\` (Object):** Cấu hình người dùng đang chọn.
* \`configMeta.valFields\`: Mảng các cột giá trị (VD: \`['doanh_thu', 'chi_phi']\`).
* \`configMeta.rowFields\`: Mảng các cột nhóm dòng.
* \`configMeta.colField\`: Tên cột dùng để chia nhóm (Nếu không chia sẽ là \`'none'\`).
* \`configMeta.aggType\`: Loại phép tính hiện tại (\`'sum'\`, \`'count'\`, \`'avg'\`).

**3. \`rawReportData\` (Array):** Mảng chứa toàn bộ dữ liệu báo cáo gốc thô (chưa qua xử lý Pivot). Thích hợp nếu bạn muốn tự dùng hàm \`map\`, \`reduce\` của riêng mình.

**4. \`columns\` (Array):** Mảng định nghĩa cột gốc của báo cáo (chứa \`field\`, \`label\`, \`type\`).

**5. Các thư viện toàn cục:**
* \`MUI\`: Toàn bộ components của Material-UI. (VD: \`<MUI.Box>\`, \`<MUI.Typography>\`, \`<MUI.Grid>\`). Chú ý: MUI là phiên bản 7 với Grid có thuộc tính size.
* \`React\`: Sử dụng để gọi Hooks. (VD: \`React.useState\`, \`React.useMemo\`).
* \`Chart\`: Component vẽ biểu đồ \`<Chart />\` từ \`react-google-charts\`.
`;

const formatDateHelper = (val) => {
  if (val === null || val === undefined || val === '') return '(Trống)';
  const dObj = new Date(val);
  if (!isNaN(dObj.getTime())) {
      const dd = String(dObj.getDate()).padStart(2, '0');
      const mm = String(dObj.getMonth() + 1).padStart(2, '0');
      const yyyy = dObj.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
  }
  return String(val);
};

export default function DataPivotViewer({ 
  data = [], columns = [], reportCode, apiCode, filterFields, reportCondition = {},
  isWidget = false, configMeta = null,handleDataScript=null,
  initialConfigId = null   // mở từ widget dashboard: chọn đúng mẫu này, ưu tiên hơn mẫu dùng lần cuối
}) {
  const authContext = useContext(AuthContext);
  const quickAction = useQuickAction();

  // ================= 0. AUTO-AUGMENT DATE COLUMNS (TỰ ĐỘNG THÊM NĂM/THÁNG) =================
  const { augmentedColumns, augmentedData } = useMemo(() => {
    let newCols = [...columns.filter(c=>c.type!="Button")];
    let newData = [...data];
    let addedCols = [];

    const dateCols = columns.filter(c => c.type === 'Date');
    dateCols.forEach(dCol => {
        const yearField = `${dCol.field}_year`;
        const monthField = `${dCol.field}_month`;

        const hasYear = newCols.some(c => c.field === yearField);
        const hasMonth = newCols.some(c => c.field === monthField);
        if (!hasYear) {
            addedCols.push({ field: yearField, label: `Năm (${dCol.label})`, type: 'String' });
        }
        if (!hasMonth) {
            addedCols.push({ field: monthField, label: `Tháng (${dCol.label})`, type: 'String' });
        }
    });

    if (addedCols.length > 0) {
        newCols = [...newCols, ...addedCols];
        
        newData = data.map(row => {
            const newRow = { ...row };
            dateCols.forEach(dCol => {
                const val = row[dCol.field];
                const yearField = `${dCol.field}_year`;
                const monthField = `${dCol.field}_month`;
                
                if (val === null || val === undefined || val === '') {
                    newRow[yearField] = '(Trống)';
                    newRow[monthField] = '(Trống)';
                } else {
                    const d = new Date(val);
                    if (!isNaN(d.getTime())) {
                        const yyyy = d.getFullYear();
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        newRow[yearField] = String(yyyy);
                        newRow[monthField] = `${yyyy}-${mm}`; 
                    } else {
                        newRow[yearField] = '(Trống)';
                        newRow[monthField] = '(Trống)';
                    }
                }
            });
            return newRow;
        });
    }

    return { augmentedColumns: newCols, augmentedData: newData };
  }, [columns, data]);


  const dimensionCols = useMemo(() => 
    augmentedColumns.filter(c => c.type === 'String' || c.type === 'Select' || c.type === 'Date' || c.type === 'Boolean')
  , [augmentedColumns]);
  
  const measureCols = useMemo(() => 
    augmentedColumns.filter(c => c.type === 'Number')
  , [augmentedColumns]);


  // ================= 1. STATE QUẢN LÝ CẤU HÌNH & CHẾ ĐỘ XEM =================
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [displayMode, setDisplayMode] = useState(configMeta?.displayMode || 'table'); 
  
  const [script, setScript] = useState(configMeta?.script || "");
  const [showScriptEditor, setShowScriptEditor] = useState(false);

  const userToken = authContext.userInfo?.token || (typeof authContext.getToken === 'function' ? authContext.getToken() : '');
  const storageKey = `flexbiz_last_pivot_config_${authContext.userInfo?.email || 'public'}_${reportCode}`;

  const applyConfig = (configId, configList = savedConfigs) => {
    setSelectedConfigId(configId);
    if (!configId) {
        setScript(""); 
        return;
    }
    const configObj = configList.find(c => c._id === configId || c.id === configId);
    if (configObj && configObj.config) {
      try {
        const parsed = JSON.parse(configObj.config);
        setRowFields(parsed.rowFields || []);
        setValFields(parsed.valFields || []);
        setColField(parsed.colField || 'none');
        setAggType(parsed.aggType || 'sum');
        setDisplayMode(parsed.displayMode || 'table');
        setPivotFilters(parsed.pivotFilters || []); 
        setScript(parsed.script || ""); 
      } catch (err) {}
    }
  };

  const fetchConfigs = async () => {
    if (!reportCode || isWidget) return; 
    try {
      const res = await authContext.apis.asyncGetList(userToken, 'pivotconfig', { condition: { code: reportCode }, limit: 100, sort: { _id: -1 } });
      const fetchedConfigs = res?.data || res || [];
      setSavedConfigs(fetchedConfigs);

      if (fetchedConfigs.length > 0) {
          if (initialConfigId && fetchedConfigs.some(c => String(c._id || c.id) === String(initialConfigId))) {
              const found = fetchedConfigs.find(c => String(c._id || c.id) === String(initialConfigId));
              applyConfig(found._id || found.id, fetchedConfigs);
              return;
          }
          let lastUsedId = null;
          try { lastUsedId = localStorage.getItem(storageKey); } catch (e) { /* bỏ qua */ }
          if (lastUsedId && fetchedConfigs.some(c => (c._id || c.id) === lastUsedId)) {
              applyConfig(lastUsedId, fetchedConfigs);
          } else {
              applyConfig(fetchedConfigs[0]._id || fetchedConfigs[0].id, fetchedConfigs);
          }
      }
    } catch (e) { console.error("Lỗi tải cấu hình:", e); }
  };

  useEffect(() => { fetchConfigs(); }, [reportCode, isWidget]);

  // ================= 2. STATE CẤU HÌNH PIVOT ENGINE & VALIDATION CHẶT CHẼ =================
  const [pivotFilters, setPivotFilters] = useState(configMeta?.pivotFilters || []); 
  const [rowFields, setRowFields] = useState(configMeta?.rowFields || []);   
  const [valFields, setValFields] = useState(configMeta?.valFields || []);   
  const [colField, setColField] = useState(configMeta?.colField || 'none'); 
  const [aggType, setAggType]   = useState(configMeta?.aggType || 'sum');  

  useEffect(() => {
    if (isWidget && configMeta) {
      setDisplayMode(configMeta.displayMode || 'table');
      setPivotFilters(configMeta.pivotFilters || []);
      setRowFields(configMeta.rowFields || []);
      setValFields(configMeta.valFields || []);
      setColField(configMeta.colField || 'none');
      setAggType(configMeta.aggType || 'sum');
      setScript(configMeta.script || "");
    }
  }, [isWidget, configMeta]);

  // SELF-CORRECTING EFFECT: Luôn luôn quét và dọn dẹp các lựa chọn khi có bất kỳ sự thay đổi nào
  useEffect(() => {
    if (!augmentedColumns || augmentedColumns.length === 0) return;

    const validFields = new Set(augmentedColumns.map(c => c.field));
    const validDimFields = new Set(dimensionCols.map(c => c.field));
    const validMeasFields = new Set(measureCols.map(c => c.field));

    // 1. Dọn dẹp Pivot Filters: Xóa bỏ các bộ lọc gán với cột không tồn tại
    if (pivotFilters.some(f => f.field && !validFields.has(f.field))) {
        setPivotFilters(prev => prev.filter(f => !f.field || validFields.has(f.field)));
    }

    // 2. Dọn dẹp Row Fields (Nhóm Dòng)
    const validRows = rowFields.filter(f => validDimFields.has(f));
    if (validRows.length !== rowFields.length) {
        setRowFields(validRows);
    } else if (validRows.length === 0 && rowFields.length === 0 && dimensionCols.length > 0 && !isWidget && !selectedConfigId) {
        // Chỉ gán giá trị mặc định lúc ban đầu nếu chưa từng được chọn và không bị dính cấu hình widget
        setRowFields([dimensionCols[0].field]);
    }

    // 3. Dọn dẹp Val Fields (Cột tính toán)
    const validVals = valFields.filter(f => validMeasFields.has(f));
    if (validVals.length !== valFields.length) {
        setValFields(validVals);
    } else if (validVals.length === 0 && valFields.length === 0 && measureCols.length > 0 && !isWidget && !selectedConfigId) {
        setValFields([measureCols[0].field]);
    }

    // 4. Dọn dẹp Col Field (Chia Cột)
    if (colField !== 'none' && !validDimFields.has(colField)) {
        setColField('none');
    }

  }, [
    augmentedColumns, dimensionCols, measureCols, 
    pivotFilters, rowFields, valFields, colField, 
    isWidget, selectedConfigId
  ]);


  const uniqueValuesMap = useMemo(() => {
    const map = {};
    augmentedColumns.forEach(col => {
      const set = new Set();
      augmentedData.forEach(d => {
        let val = d[col.field];
        if (col.type === 'Date') {
          set.add(formatDateHelper(val));
        } else {
          set.add((val === null || val === undefined || val === '') ? '(Trống)' : String(val));
        }
      });
      
      map[col.field] = Array.from(set).sort((a, b) => {
        if (col.type === 'Date' && a !== '(Trống)' && b !== '(Trống)') {
            const partsA = a.split('/');
            const partsB = b.split('/');
            if (partsA.length === 3 && partsB.length === 3) {
                const dateA = new Date(`${partsA[2]}-${partsA[1]}-${partsA[0]}`);
                const dateB = new Date(`${partsB[2]}-${partsB[1]}-${partsB[0]}`);
                return dateA - dateB;
            }
        }
        return String(a).localeCompare(String(b));
      });
    });
    return map;
  }, [augmentedData, augmentedColumns]);


  // ================= 3. LOGIC LƯU VÀ TẢI CẤU HÌNH =================
  const handleSelectConfig = (e) => {
    const configId = e.target.value;
    applyConfig(configId);
    if (!isWidget) {
        if (configId) localStorage.setItem(storageKey, configId);
        else localStorage.removeItem(storageKey);
    }
  };

  const handleSaveConfig = () => {
    const currentPivotSettings = { apiCode, rowFields, valFields, colField, aggType, displayMode, pivotFilters, filterFields, reportCondition
      ,columns
      , script,handleDataScript
    }; 
    const configString = JSON.stringify(currentPivotSettings,null,2);
    
    const formFields = [
      { field: 'title', label: 'Tên góc nhìn', type: 'String', required: true, gridEdit: { xs: 12 } },
      { field: 'code', label: 'Mã', type: 'String', hideInForm: true },
      { field: 'config', label: 'Cấu hình', type: 'String', hideInForm: true }
    ];
    const onSuccess = (savedItem) => {
      const newId = savedItem._id || savedItem.id;
      localStorage.setItem(storageKey, newId); 
      fetchConfigs();
      if (authContext.alert) authContext.alert("Đã lưu cấu hình thành công!", null, "success");
    };
    if (selectedConfigId) {
      const existingConf = savedConfigs.find(c => c._id === selectedConfigId || c.id === selectedConfigId);
      quickAction.openEdit('pivotconfig', formFields, 'Cập nhật cấu hình', { ...existingConf, config: configString }, onSuccess);
    } else {
      quickAction.openAdd('pivotconfig', formFields, 'Lưu cấu hình mới', { code: reportCode, config: configString }, onSuccess);
    }
  };

  const handleSaveAsNew = () => { setSelectedConfigId(''); setTimeout(() => handleSaveConfig(), 100); };

  // ================= 4. LOGIC XỬ LÝ BỘ LỌC =================
  const handleAddFilter = () => setPivotFilters([...pivotFilters, { field: '', operator: 'in', values: [], value: '' }]);
  const handleUpdateFilterField = (index, field) => {
    const newF = [...pivotFilters];
    newF[index] = { ...newF[index], field, values: [], value: '' }; 
    setPivotFilters(newF);
  };
  const handleUpdateFilterOperator = (index, operator) => {
    const newF = [...pivotFilters];
    newF[index].operator = operator;
    setPivotFilters(newF);
  };
  const handleUpdateFilterValues = (index, values) => {
    const newF = [...pivotFilters];
    newF[index].values = typeof values === 'string' ? values.split(',') : values;
    setPivotFilters(newF);
  };
  const handleUpdateFilterSingleValue = (index, value) => {
    const newF = [...pivotFilters];
    newF[index].value = value;
    setPivotFilters(newF);
  };
  const handleRemoveFilter = (index) => setPivotFilters(pivotFilters.filter((_, i) => i !== index));

  // ================= 5. PIVOT ENGINE =================
  const pivotData = useMemo(() => {
    if (!augmentedData.length || !rowFields.length || !valFields.length) return null;
    
    const colTypeMap = augmentedColumns.reduce((acc, c) => ({ ...acc, [c.field]: c.type }), {});

    const getVal = (row, field) => { 
        const val = row[field]; 
        if (val === null || val === undefined || val === '') return '(Trống)';
        if (colTypeMap[field] === 'Date') return formatDateHelper(val);
        return String(val); 
    };

    const workingData = pivotFilters.length > 0 ? augmentedData.filter(d => {
      return pivotFilters.every(f => {
        if (!f.field) return true; 

        const rawVal = d[f.field];
        const cellValStr = getVal(d, f.field);
        const cellValNum = Number(rawVal);
        const op = f.operator || 'in';

        if (op === 'in') {
          if (!f.values || f.values.length === 0) return true;
          return f.values.includes(cellValStr);
        }

        if (f.value === undefined || f.value === '') return true;
        
        let compareValStr = String(f.value);
        let compareValNum = Number(f.value);

        if (colTypeMap[f.field] === 'Date' && ['>', '<', '>=', '<='].includes(op)) {
            const parseDateString = (str) => {
                const parts = str.split('/');
                if (parts.length === 3) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
                return new Date(str).getTime(); 
            };
            const cVal = parseDateString(cellValStr);
            const tVal = parseDateString(compareValStr);
            if (!isNaN(cVal) && !isNaN(tVal)) {
                switch (op) {
                    case '>': return cVal > tVal;
                    case '>=': return cVal >= tVal;
                    case '<': return cVal < tVal;
                    case '<=': return cVal <= tVal;
                }
            }
        }

        switch (op) {
          case '=': return cellValStr === compareValStr;
          case '<>': return cellValStr !== compareValStr;
          case '>': return !isNaN(cellValNum) && !isNaN(compareValNum) ? cellValNum > compareValNum : cellValStr > compareValStr;
          case '>=': return !isNaN(cellValNum) && !isNaN(compareValNum) ? cellValNum >= compareValNum : cellValStr >= compareValStr;
          case '<': return !isNaN(cellValNum) && !isNaN(compareValNum) ? cellValNum < compareValNum : cellValStr < compareValStr;
          case '<=': return !isNaN(cellValNum) && !isNaN(compareValNum) ? cellValNum <= compareValNum : cellValStr <= compareValStr;
          case 'like': return cellValStr.toLowerCase().includes(compareValStr.toLowerCase());
          default: return true;
        }
      });
    }) : augmentedData;

    if (workingData.length === 0){
      return { isEmpty: true }; 
    }

    const rowKeyMap = new Map();
    const colKeySet = new Set();

    workingData.forEach(d => {
        const rArr = rowFields.map(f => getVal(d, f));
        rowKeyMap.set(JSON.stringify(rArr), rArr);
        if (colField !== 'none') colKeySet.add(getVal(d, colField));
    });

    const compareValues = (a, b, type) => {
        if (type === 'Date' && a !== '(Trống)' && b !== '(Trống)') {
            const partsA = String(a).split('/');
            const partsB = String(b).split('/');
            if (partsA.length === 3 && partsB.length === 3) {
                const dateA = new Date(`${partsA[2]}-${partsA[1]}-${partsA[0]}`).getTime();
                const dateB = new Date(`${partsB[2]}-${partsB[1]}-${partsB[0]}`).getTime();
                return dateA - dateB;
            }
        }
        return String(a).localeCompare(String(b));
    };

    const rowKeysStr = Array.from(rowKeyMap.keys()).sort((aStr, bStr) => {
        const aArr = JSON.parse(aStr);
        const bArr = JSON.parse(bStr);
        for(let i = 0; i < rowFields.length; i++) {
            const field = rowFields[i];
            const type = colTypeMap[field];
            const cmp = compareValues(aArr[i], bArr[i], type);
            if (cmp !== 0) return cmp;
        }
        return 0;
    });

    const rowKeys = rowKeysStr.map(k => ({ str: k, arr: rowKeyMap.get(k) }));
    
    const colKeys = colField !== 'none' 
      ? Array.from(colKeySet).sort((a, b) => compareValues(a, b, colTypeMap[colField])) 
      : ['Tổng'];

    const matrix = {}; const colTotals = {}; const grandTotal = {};
    const initVals = () => { const obj = {}; valFields.forEach(v => obj[v] = { sum: 0, count: 0 }); return obj; };

    colKeys.forEach(c => colTotals[c] = initVals());
    valFields.forEach(v => grandTotal[v] = { sum: 0, count: 0 });
    rowKeys.forEach(r => { matrix[r.str] = { _rowTotal: initVals() }; colKeys.forEach(c => matrix[r.str][c] = initVals()); });

    workingData.forEach(d => {
        const rStr = JSON.stringify(rowFields.map(f => getVal(d, f)));
        const c = colField !== 'none' ? getVal(d, colField) : 'Tổng';
        valFields.forEach(vField => {
            const v = Number(d[vField]) || 0;
            matrix[rStr][c][vField].sum += v; matrix[rStr][c][vField].count += 1;
            matrix[rStr]._rowTotal[vField].sum += v; matrix[rStr]._rowTotal[vField].count += 1;
            colTotals[c][vField].sum += v; colTotals[c][vField].count += 1;
            grandTotal[vField].sum += v; grandTotal[vField].count += 1;
        });
    });

    const calculateAgg = (stats) => {
        if (stats.count === 0) return 0;
        if (aggType === 'sum') return stats.sum;
        if (aggType === 'count') return stats.count;
        if (aggType === 'avg') return stats.sum / stats.count;
        return 0;
    };
    return { isEmpty: false, rowKeys, colKeys, matrix, colTotals, grandTotal, calculateAgg };
  }, [augmentedData, rowFields, colField, valFields, aggType, pivotFilters, augmentedColumns]);


  // ================= 6. CHART DATA ENGINE =================
  const getColLabel = (field) => augmentedColumns.find(c => c.field === field)?.label || field;

  const chartData = useMemo(() => {
    if (!pivotData || pivotData.isEmpty || displayMode === 'table' || displayMode === 'card' || displayMode === 'custom') return null;

    const headerRow = ['Nhóm'];
    if (colField !== 'none') {
      pivotData.colKeys.forEach(c => { valFields.forEach(v => headerRow.push(`${c} - ${getColLabel(v)}`)); });
    } else {
      valFields.forEach(v => headerRow.push(getColLabel(v)));
    }

    const dataRows = pivotData.rowKeys.map(r => {
      const rowLabel = r.arr.join(' - '); 
      const rowArr = [rowLabel];

      if (colField !== 'none') {
        pivotData.colKeys.forEach(c => { valFields.forEach(v => rowArr.push(pivotData.calculateAgg(pivotData.matrix[r.str][c][v]))); });
      } else {
        valFields.forEach(v => rowArr.push(pivotData.calculateAgg(pivotData.matrix[r.str]['Tổng'][v])));
      }
      return rowArr;
    });

    return [headerRow, ...dataRows];
  }, [pivotData, displayMode, colField, valFields, aggType]);

  const formatNumber = (num) => {
    if (num === 0 && aggType !== 'count') return '-';
    return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: aggType === 'avg' ? 2 : 0 }).format(num);
  };

  // ================= RENDER =================
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: isWidget ? 'transparent' : 'background.paper', borderRadius: isWidget ? 0 : 2, border: isWidget ? 'none' : '1px solid #e0e0e0', overflow: 'hidden' }}>
      
      {!isWidget && (
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>Phân tích Đa chiều</Typography>

              <ToggleButtonGroup size="small" value={displayMode} exclusive onChange={(e, val) => { if (val) setDisplayMode(val); }} sx={{ height: 32, bgcolor: 'white' }}>
                <Tooltip title="Dạng Bảng"><ToggleButton value="table"><TableViewIcon fontSize="small" /></ToggleButton></Tooltip>
                <Tooltip title="Dạng Thẻ"><ToggleButton value="card"><GridViewIcon fontSize="small" /></ToggleButton></Tooltip>
                <Tooltip title="Biểu đồ Cột"><ToggleButton value="ColumnChart"><BarChartIcon fontSize="small" /></ToggleButton></Tooltip>
                <Tooltip title="Biểu đồ Tròn"><ToggleButton value="PieChart"><PieChartIcon fontSize="small" /></ToggleButton></Tooltip>
                <Tooltip title="Giao diện Tùy chỉnh (Script)"><ToggleButton value="custom"><CodeIcon fontSize="small" /></ToggleButton></Tooltip>
              </ToggleButtonGroup>

              <Tooltip title="Soạn thảo mã nguồn hiển thị">
                <IconButton size="small" onClick={() => setShowScriptEditor(true)} sx={{ bgcolor: script ? 'primary.light' : 'white', border: '1px solid #e0e0e0', color: script ? 'white' : 'inherit' }}>
                  <CodeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 220, bgcolor: 'white' }}>
                <Select displayEmpty value={selectedConfigId} onChange={handleSelectConfig} sx={{ height: 32, fontSize: '0.85rem' }}>
                  <MenuItem value="" sx={{ fontStyle: 'italic' }}>--- Cấu hình tùy chỉnh ---</MenuItem>
                  {savedConfigs.map(c => (<MenuItem key={c._id || c.id} value={c._id || c.id}>{c.title}</MenuItem>))}
                </Select>
              </FormControl>
              <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSaveConfig} sx={{ height: 32, textTransform: 'none', fontWeight: 'bold' }}>
                {selectedConfigId ? 'Cập nhật' : 'Lưu'}
              </Button>
              {selectedConfigId && (
                <Tooltip title="Lưu cấu hình mới">
                  <IconButton onClick={handleSaveAsNew} size="small" sx={{ bgcolor: 'white', border: '1px solid #e0e0e0' }}><AddCircleOutlineIcon fontSize="small" /></IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: pivotFilters.length > 0 ? 1.5 : 0 }}>
              <Button size="small" variant="outlined" startIcon={<FilterAltIcon />} onClick={handleAddFilter} sx={{ textTransform: 'none', borderRadius: 2, height: 28, fontSize: '0.8rem', bgcolor: 'white' }}>
                Thêm bộ lọc dữ liệu
              </Button>
            </Box>
            
            {pivotFilters.length > 0 && (
              <Grid container spacing={1.5}>
                {pivotFilters.map((f, idx) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} key={idx}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', bgcolor: 'white', p: 0.5, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                      <FormControl size="small" sx={{ flex: 1.2 }}>
                        <Select displayEmpty value={f.field} onChange={(e) => handleUpdateFilterField(idx, e.target.value)} sx={{ fontSize: '0.8rem', height: 30 }}>
                          <MenuItem value="" disabled>Chọn cột...</MenuItem>
                          {augmentedColumns.map(c => <MenuItem key={c.field} value={c.field}>{c.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ flex: 1 }} disabled={!f.field}>
                        <Select value={f.operator || 'in'} onChange={(e) => handleUpdateFilterOperator(idx, e.target.value)} sx={{ fontSize: '0.8rem', height: 30 }}>
                          <MenuItem value="in">Thuộc</MenuItem>
                          <MenuItem value="=">Bằng (=)</MenuItem>
                          <MenuItem value="<>">Khác (&lt;&gt;)</MenuItem>
                          <MenuItem value=">">Lớn hơn (&gt;)</MenuItem>
                          <MenuItem value=">=">Lớn hơn hoặc bằng (&gt;=)</MenuItem>
                          <MenuItem value="<">Nhỏ hơn (&lt;)</MenuItem>
                          <MenuItem value="<=">Nhỏ hơn hoặc bằng (&lt;=)</MenuItem>
                          <MenuItem value="like">Chứa (like)</MenuItem>
                        </Select>
                      </FormControl>
                      {(!f.operator || f.operator === 'in') ? (
                        <FormControl size="small" sx={{ flex: 1.5 }} disabled={!f.field}>
                          <Select multiple displayEmpty value={f.values || []} onChange={(e) => handleUpdateFilterValues(idx, e.target.value)} renderValue={(selected) => selected.length === 0 ? <span style={{ color: '#aaa' }}>Tất cả...</span> : `${selected.length} giá trị`} sx={{ fontSize: '0.8rem', height: 30 }}>
                            {uniqueValuesMap[f.field]?.map(val => (
                              <MenuItem key={val} value={val} sx={{ py: 0, minHeight: 32 }}>
                                <Checkbox checked={(f.values || []).indexOf(val) > -1} size="small" sx={{ p: 0.5 }} />
                                <ListItemText primary={val} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField size="small" variant="outlined" disabled={!f.field} value={f.value || ''} onChange={(value) => handleUpdateFilterSingleValue(idx, value)} placeholder="Nhập giá trị..." sx={{ flex: 1.5, '& .MuiOutlinedInput-root': { height: 30, fontSize: '0.8rem' } }} />
                      )}
                      <IconButton size="small" onClick={() => handleRemoveFilter(idx)} color="error" sx={{ p: 0.5 }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          <Divider sx={{ my: 2, borderStyle: 'dashed', borderColor: '#cfd8dc' }} />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3, md: 3 }}>
              <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                <InputLabel>Nhóm Dòng (Trục Y / Trục X Biểu đồ)</InputLabel>
                <Select multiple value={rowFields} onChange={e => setRowFields(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)} input={<OutlinedInput label="Nhóm Dòng (Trục Y / Trục X Biểu đồ)" />} renderValue={(selected) => (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selected.map((value) => (<Chip key={value} label={getColLabel(value)} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />))}</Box>)}>
                  {dimensionCols.map(c => (<MenuItem key={c.field} value={c.field}><Checkbox checked={rowFields.indexOf(c.field) > -1} size="small" /><ListItemText primary={c.label} /></MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 3 }}>
              <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                <InputLabel>Tính toán (Values)</InputLabel>
                <Select multiple value={valFields} onChange={e => setValFields(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)} input={<OutlinedInput label="Tính toán (Values)" />} renderValue={(selected) => (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selected.map((value) => (<Chip key={value} label={getColLabel(value)} size="small" color="primary" sx={{ height: 20, fontSize: '0.75rem' }} />))}</Box>)}>
                  {measureCols.map(c => (<MenuItem key={c.field} value={c.field}><Checkbox checked={valFields.indexOf(c.field) > -1} size="small" /><ListItemText primary={c.label} /></MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 3 }}>
              <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                <InputLabel>Chia Cột (Nhóm Biểu đồ)</InputLabel>
                <Select value={colField} label="Chia Cột (Nhóm Biểu đồ)" onChange={(e) => setColField(e.target.value)}>
                  <MenuItem value="none" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>-- Không phân rã --</MenuItem>
                  {dimensionCols.filter(c => !rowFields.includes(c.field)).map(c => <MenuItem key={c.field} value={c.field}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 3 }}>
              <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                <InputLabel>Loại phép tính</InputLabel>
                <Select value={aggType} label="Loại phép tính" onChange={(e) => setAggType(e.target.value)}>
                  <MenuItem value="sum">Tổng cộng (Sum)</MenuItem>
                  <MenuItem value="count">Đếm số dòng (Count)</MenuItem>
                  <MenuItem value="avg">Trung bình (Average)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* KHU VỰC 2: HIỂN THỊ KẾT QUẢ */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!pivotData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, color: 'text.secondary' }}>
            Vui lòng chọn ít nhất 1 cột Dòng và 1 cột Giá trị
          </Box>
        ) : pivotData.isEmpty ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, color: IOS_BLUE, fontWeight: 'bold' }}>
            Không có dữ liệu nào phù hợp với bộ lọc hiện tại!
          </Box>
        ) : displayMode === 'custom' ? (
          
          <DynamicCustomPivot 
             script={script} 
             pivotData={pivotData} 
             rawReportData={augmentedData} 
             columns={augmentedColumns} 
             configMeta={{ rowFields, valFields, colField, aggType }} 
             context={authContext}
          />

        ) : displayMode === 'table' ? (
          
          <TableContainer sx={{ flexGrow: 1, overflow: 'auto', ...macScrollbarSx }}>
            <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse', minWidth: 800 }}>
              
              <TableHead sx={{ '& th': { whiteSpace: 'nowrap', boxSizing: 'border-box' } }}>
                <TableRow sx={{ height: 40 }}>
                  {rowFields.map((rField, idx) => (
                    <TableCell key={rField} rowSpan={2} sx={{ position: 'sticky', top: 0, left: idx * 100, zIndex: 10, bgcolor: '#e3f2fd', border: '1px solid #cfd8dc', fontWeight: 'bold' }}>
                      {getColLabel(rField)}
                    </TableCell>
                  ))}
                  
                  {colField !== 'none' ? (
                    pivotData.colKeys.map(c => (
                      <TableCell key={c} colSpan={valFields.length} align="center" sx={{ position: 'sticky', top: 0, zIndex: 5, height: 40, bgcolor: '#e3f2fd', border: '1px solid #cfd8dc', fontWeight: 'bold', maxWidth: valFields.length * 150 }}>
                        <Tooltip title={c} arrow placement="top">
                          <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{c}</Box>
                        </Tooltip>
                      </TableCell>
                    ))
                  ) : (
                    <TableCell colSpan={valFields.length} align="center" sx={{ position: 'sticky', top: 0, zIndex: 5, height: 40, bgcolor: '#e3f2fd', border: '1px solid #cfd8dc', fontWeight: 'bold' }}>Giá trị phân tích</TableCell>
                  )}
                  
                  {colField !== 'none' && (
                    <TableCell colSpan={valFields.length} align="center" sx={{ position: 'sticky', top: 0, right: 0, zIndex: 10, height: 40, bgcolor: '#e3f2fd', border: '1px solid #cfd8dc', fontWeight: 'bold' }}>
                      TỔNG CỘNG ({aggType.toUpperCase()})
                    </TableCell>
                  )}
                </TableRow>
                
                <TableRow sx={{ height: 40 }}>
                  {colField !== 'none' ? (
                    pivotData.colKeys.map(c => (valFields.map(v => (
                      <TableCell key={`${c}-${v}`} align="right" sx={{ position: 'sticky', top: 40, zIndex: 5, height: 40, bgcolor: '#f8f9fa', border: '1px solid #cfd8dc', fontWeight: 'bold', fontSize: '0.8rem', minWidth: 100 }}>{getColLabel(v)}</TableCell>
                    ))))
                  ) : (
                    valFields.map(v => (
                      <TableCell key={v} align="right" sx={{ position: 'sticky', top: 40, zIndex: 5, height: 40, bgcolor: '#f8f9fa', border: '1px solid #cfd8dc', fontWeight: 'bold', fontSize: '0.8rem', minWidth: 100 }}>{getColLabel(v)}</TableCell>
                    ))
                  )}
                  
                  {colField !== 'none' && (valFields.map(v => (
                    <TableCell key={`total-${v}`} align="right" sx={{ position: 'sticky', top: 40, right: 0, zIndex: 10, height: 40, bgcolor: '#f8f9fa', border: '1px solid #cfd8dc', fontWeight: 'bold', fontSize: '0.8rem', minWidth: 100 }}>{getColLabel(v)}</TableCell>
                  )))}
                </TableRow>
              </TableHead>

              <TableBody>
                {pivotData.rowKeys.map(r => (
                  <TableRow key={r.str} hover>
                    {r.arr.map((rowVal, idx) => (<TableCell key={idx} sx={{ position: 'sticky', left: idx * 100, zIndex: 2, bgcolor: 'white', border: '1px solid #e0e0e0', fontWeight: '500' }}>{rowVal}</TableCell>))}
                    {colField !== 'none' ? (pivotData.colKeys.map(c => (valFields.map(v => (<TableCell key={`${c}-${v}`} align="right" sx={{ border: '1px solid #e0e0e0' }}>{formatNumber(pivotData.calculateAgg(pivotData.matrix[r.str][c][v]))}</TableCell>))))) : (valFields.map(v => (<TableCell key={v} align="right" sx={{ border: '1px solid #e0e0e0' }}>{formatNumber(pivotData.calculateAgg(pivotData.matrix[r.str]['Tổng'][v]))}</TableCell>)))}
                    {colField !== 'none' && (valFields.map(v => (<TableCell key={`rowTotal-${v}`} align="right" sx={{ position: 'sticky', right: 0, zIndex: 2, bgcolor: '#fff9c4', border: '1px solid #e0e0e0', fontWeight: 'bold', color: IOS_BLUE }}>{formatNumber(pivotData.calculateAgg(pivotData.matrix[r.str]._rowTotal[v]))}</TableCell>)))}
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={rowFields.length} sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: '#e3f2fd', border: '1px solid #cfd8dc', fontWeight: 'bold', textTransform: 'uppercase' }}>Tổng Toàn Bộ</TableCell>
                  {colField !== 'none' ? (pivotData.colKeys.map(c => (valFields.map(v => (<TableCell key={`colTotal-${c}-${v}`} align="right" sx={{ bgcolor: '#e3f2fd', border: '1px solid #cfd8dc', fontWeight: 'bold', color: IOS_BLUE }}>{formatNumber(pivotData.calculateAgg(pivotData.colTotals[c][v]))}</TableCell>))))) : (valFields.map(v => (<TableCell key={`colTotal-none-${v}`} align="right" sx={{ bgcolor: '#e3f2fd', border: '1px solid #cfd8dc', fontWeight: 'bold', color: IOS_BLUE }}>{formatNumber(pivotData.calculateAgg(pivotData.grandTotal[v]))}</TableCell>)))}
                  {colField !== 'none' && (valFields.map(v => (<TableCell key={`grandTotal-${v}`} align="right" sx={{ position: 'sticky', right: 0, zIndex: 4, bgcolor: '#bbdefb', border: '1px solid #cfd8dc', fontWeight: 'bold', color: IOS_BLUE, fontSize: '1.05rem' }}>{formatNumber(pivotData.calculateAgg(pivotData.grandTotal[v]))}</TableCell>)))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

        ) : displayMode === 'card' ? (
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: isWidget ? 1 : 2, bgcolor: isWidget ? 'transparent' : '#f4f6f8', ...macScrollbarSx }}>
            <Grid container spacing={2}>
              {pivotData.rowKeys.map(r => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={r.str}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: 2 }}>
                    <Box sx={{ px: 2, py: 1.5, bgcolor: '#e3f2fd', borderBottom: '1px solid #bbdefb' }}>
                      <Typography variant="subtitle2" fontWeight="bold" color={IOS_BLUE} noWrap>
                        {r.arr.join(' - ')}
                      </Typography>
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 0, '&:last-child': { pb: 0 } }}>
                      {colField !== 'none' ? (
                        pivotData.colKeys.map((c, cIdx) => (
                          <Box key={c} sx={{ borderBottom: cIdx < pivotData.colKeys.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                            <Tooltip title={c} arrow placement="top-start">
                              <Typography variant="caption" noWrap sx={{ px: 2, pt: 1, display: 'block', fontWeight: 'bold', color: 'text.secondary', bgcolor: '#fafafa' }}>{c}</Typography>
                            </Tooltip>
                            {valFields.map(v => (
                              <Box key={v} sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 0.75, borderBottom: '1px dashed #f5f5f5', '&:last-child': { borderBottom: 'none' } }}>
                                <Typography variant="body2" color="text.secondary">{getColLabel(v)}</Typography>
                                <Typography variant="body2" fontWeight="bold">{formatNumber(pivotData.calculateAgg(pivotData.matrix[r.str][c][v]))}</Typography>
                              </Box>
                            ))}
                          </Box>
                        ))
                      ) : (
                        valFields.map(v => (
                          <Box key={v} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' } }}>
                            <Typography variant="body2" color="text.secondary">{getColLabel(v)}</Typography>
                            <Typography variant="body1" fontWeight="bold" color="text.primary">
                              {formatNumber(pivotData.calculateAgg(pivotData.matrix[r.str]['Tổng'][v]))}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, p: isWidget ? 0 : 3, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Chart
              chartType={displayMode}
              width="100%"
              height="100%"
              data={chartData}
              options={{
                title: isWidget ? '' : `Biểu đồ ${aggType === 'sum' ? 'Tổng' : aggType === 'avg' ? 'Trung bình' : 'Số lượng'} theo ${rowFields.map(getColLabel).join(' và ')}`,
                titleTextStyle: { fontSize: 16, bold: true, color: '#333' },
                chartArea: { width: '85%', height: '75%' },
                hAxis: { title: rowFields.map(getColLabel).join(' / '), textStyle: { fontSize: 12 }, slantedText: true, slantedTextAngle: 45 },
                vAxis: { title: 'Giá trị', format: 'short' },
                legend: { position: displayMode === 'PieChart' ? 'right' : 'top' },
                animation: { startup: true, duration: 500, easing: 'out' },
                pieHole: 0.4, is3D: false,
              }}
              loader={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>}
            />
          </Box>
        )}
      </Box>

      {/* ================= DIALOG TRÌNH SOẠN THẢO CODE ================= */}
      <ManagedModal
        type="dialog"
        open={showScriptEditor} 
        onClose={() => setShowScriptEditor(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { height: '85vh', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8f9fa' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">Customize View Script</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">Sử dụng React, MUI để thiết kế lại kết quả</Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, bgcolor: '#ffffff' }}>
          <CodeEditor 
            data={script} 
            onChange={(e, newVal) => setScript(newVal)} 
            language="javascript"
            helperText={HELP_TEXT_CONTENT}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
          <Button onClick={() => setShowScriptEditor(false)} color="inherit" sx={{ textTransform: 'none' }}>Đóng</Button>
          <Button 
            variant="contained" 
            onClick={() => { setDisplayMode('custom'); setShowScriptEditor(false); }} 
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Áp dụng và xem thử
          </Button>
        </DialogActions>
      </ManagedModal>

    </Box>
  );
}